import {ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output, ViewChild} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {TableRequest} from '../../model/api/table-request';
import {CcBaseComponent} from '../cc-base.component';
import {TableColumnConfig} from '../../model/api/table-column-config';
import {FormFieldGroupConfig} from '../../model/api/form-field-group-config';
import {CommonCrudService} from '../../services/common/common-crud.service';
import {TableButtonConfig} from '../../model/api/table-button-config';
import {CCRoles, CCRolesType} from "../../model/security/cc-roles-type";
import {CommonFieldService} from "../../services/common/common-field.service";
import {CcTableComponent} from "../table/cc-table.component";
import {CcBaseFormComponent} from "../cc-base-form.component";
import {takeUntil, timer} from "rxjs";

@Component({
  template: '',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class CrudBaseComponent<T = any> extends CcBaseFormComponent<T> {

  CCRoles = CCRoles

  @Input()
  fieldId: string;

  // translation part key
  @Input()
  transPartKey: string;

  @Input()
  crudService: CommonCrudService<any>;

  @Input()
  newRecord: T;

  // table
  @Output()
  formRecordChange = new EventEmitter<T>();

  @Input()
  tableConfig: TableColumnConfig[] = [];

  @Input()
  headerButtonConfig: TableButtonConfig[] = [];

  @Input()
  footerButtonConfig: TableButtonConfig[] = [];

  @Input()
  contextMenuConfig: MenuItem[] = [];

  @Input()
  selectedRecords: T[] = [];

  @Input()
  records: T[] = [];
  @Output()
  recordsChange = new EventEmitter<T[]>();

  @Input()
  totalRecords: number = 0;

  @Input()
  tableLoading: boolean = true;

  @Input()
  tableCurrentRequest: TableRequest = new TableRequest(undefined);
  @Output()
  tableCurrentRequestChange = new EventEmitter<TableRequest>();

  // form
  @Input()
  formConfig: FormFieldGroupConfig[] | any = [];

  @Input()
  dialogFormTitle: string;

  @Input()
  formLoading: boolean = false;

  @Input()
  formSubmitted: boolean = true;

  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<boolean>();
  @Output()
  onSave = new EventEmitter<boolean>();

  @Input()
  prepareForCreate: Function;

  @Input()
  prepareForEdit: Function;

  @Input()
  prepareForClone: Function;

  @Input()
  prepareSelectedIds: Function;

  @Input()
  beforeSave: Function;

  @Input()
  globalErrorMessage: string = '';

  @Input()
  globalInfoMessage: string = '';

  @Input()
  writeRoles: CCRolesType.Roles[];

  @Input()
  deleteRoles: CCRolesType.Roles[];

  @Input()
  autoReloadTimeOut: number = 0;

  @Output()
  onContextMenuShow = new EventEmitter<any>();
  @Output()
  onTableResultGet = new EventEmitter<any>();

  @ViewChild('ccTable')
  public ccTable: CcTableComponent;

  isReadOnly: boolean;

  constructor(protected cdref: ChangeDetectorRef,
              protected messageService: MessageService,
              protected confirmationService: ConfirmationService,
              protected translateService: TranslateService, protected commonFieldService: CommonFieldService) {
    super(cdref, messageService, translateService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.commonFieldService.clearStorage();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected initConfigs() {
    super.initConfigs();
    if (!this.getIsReadOnly()) {
      this.headerButtonConfig = [
        {
          tooltip: this.getTranslation('common.table.create_button'),
          icon: 'fas fa-plus',
          class: ' p-button-success',
          command: () => this.create(),
          enabled: () => true
        },
        {
          tooltip: this.getTranslation('common.table.edit_button'),
          icon: 'fas fa-pen',
          class: ' p-button-info',
          command: () => this.edit(),
          enabled: () => this.hasOneSelection()
        },
        {
          tooltip: this.getTranslation('common.table.clone_button'),
          icon: 'fas fa-clone',
          class: ' p-button-help',
          command: () => this.clone(),
          enabled: () => this.hasOneSelection()
        },];
      if (this.canDelete()) {
        this.headerButtonConfig.push(
          {
            tooltip: this.getTranslation('common.table.delete_button'),
            icon: 'fas fa-trash-alt',
            class: ' p-button-danger',
            command: () => this.delete(),
            enabled: () => this.hasMultipleSelection()
          }
        );
      }
      this.footerButtonConfig = [
        // {
        //   tooltip: this.getTranslation('common.table.create_button'),
        //   icon: 'fas fa-plus',
        //   class: ' p-button-success',
        //   command: () => this.create(),
        //   enabled: () => true
        // }
      ];
      this.contextMenuConfig = [
        {
          id: 'edit',
          label: this.getTranslation('common.table.edit_button'), icon: 'fas fa-pen', command: () => {
            this.edit();
          }
        },
        {
          id: 'clone',
          label: this.getTranslation('common.table.clone_button'), icon: 'fas fa-clone', command: () => {
            this.clone();
          }
        },
        {
          separator: true
        },
      ];
      if (this.canDelete()) {
        this.contextMenuConfig.push(
          {
            id: 'delete',
            label: this.getTranslation('common.table.delete_button'), icon: 'fas fa-trash-alt', command: () => {
              this.delete();
            }
          });
      }
    } else {
      this.headerButtonConfig = [
        {
          'tooltip': this.translateService.instant('common.table.detail_button'),
          'icon': 'fas fa-eye',
          class: ' p-button-success',
          command: records => {
            // console.log("-------> this.selectedRecords "+JSON.stringify(this.selectedRecords));
            // console.log("-------> this.formRecord "+JSON.stringify(this.formRecord));
            // @ts-ignore
            this.formRecord = this.selectedRecords[0];
            this.showFormDialog()
          },
          enabled: () => this.selectedRecords.length == 1
        }
      ];
      this.contextMenuConfig = [
        {
          label: this.translateService.instant('common.table.detail_button'),
          icon: 'fas fa-eye',
          command: () => {
            this.showFormDialog();
          }
        }
      ];
    }
    this.reloadFieldValueMap();
  }

  getSelectedIds() {
    // @ts-ignore
    if (this.prepareSelectedIds) {
      return this.prepareSelectedIds();
    } else {
      return this.selectedRecords.map(record => record[this.fieldId]);
    }
  }

  delete() {
    this.tableLoading = true;
    this.confirmationService.confirm({
      header: this.getTranslation('common.table.delete_item')
        + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + (this.selectedRecords.length === 1 ? '' : 's'),
      message: this.selectedRecords.length > 1 ? (this.getTranslation('common.dialog.confirm_delete_plu')
          + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + 's?')
        : (this.getTranslation('common.dialog.confirm_delete_sing') + ' '
          + this.getTranslation('page.' + this.transPartKey + '.item_name') + '?'),
      icon: 'fas fa-exclamation-triangle',
      acceptLabel: this.getTranslation('common.dialog.delete'),
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      rejectLabel: this.getTranslation('common.dialog.cancel'),
      rejectButtonStyleClass: 'p-button-warning p-button-sm',
      accept: () => {
        const recordIds = this.getSelectedIds();
        this.crudService.deleteRecord(recordIds).subscribe(apiValidation => {
          if (apiValidation.success) {
            if (this.autoReloadTimeOut <= 0)
              this.loadRecords();
            this.successMessage(this.getTranslation('page.' + this.transPartKey + '.item_name')
              + (this.selectedRecords.length === 1 ? '' : 's')
              + ' ' + this.getTranslation('common.message.deleted'));
            this.selectedRecords = [];
          } else {
            this.errorMessage(apiValidation.message);
            this.tableLoading = false;
          }
        });
      },
      reject: () => {
        this.tableLoading = false;
      }
    });
  }

  create(): void {
    this.formRecord = Object.assign({}, this.newRecord);
    if (this.prepareForCreate) {
      this.prepareForCreate();
    }
    this.formRecordChange.emit(this.formRecord);
    this.dialogFormTitle = this.getTranslation('common.table.create_item') + ' '
      + this.getTranslation('page.' + this.transPartKey + '.item_name');

    this.showFormDialog();
  }

  public edit(record: T = undefined) {
    if (record) {
      this.formRecord = record;
    }
    if (this.selectedRecords.length === 1) {
      this.formRecord = JSON.parse(JSON.stringify(this.selectedRecords[0]));
    }
    if (this.formRecord) {
      if (this.prepareForEdit) {
        this.prepareForEdit();
      }
      this.formRecordChange.emit(this.formRecord);
      this.dialogFormTitle = this.getTranslation('common.table.edit_item') + ' '
        + this.getTranslation('page.' + this.transPartKey + '.item_name');
      if (this.formRecord) {
        this.showFormDialog();
      }
    }
  }

  clone() {
    if (this.selectedRecords.length === 1) {
      this.formRecord = JSON.parse(JSON.stringify(this.selectedRecords[0]));
    }
    if (this.formRecord) {
      if (this.prepareForClone) {
        this.prepareForClone();
      } else {
        this.formRecord[this.fieldId] = this.newRecord[this.fieldId];
      }
      this.formRecordChange.emit(this.formRecord);
      this.dialogFormTitle = this.getTranslation('common.table.clone_item') + ' '
        + this.getTranslation('page.' + this.transPartKey + '.item_name');
      this.showFormDialog();
    }
  }

  save(reload: boolean = true) {
    this.tableLoading = true;
    this.formLoading = true;
    this.formSubmitted = true;

    if (this.beforeSave) {
      this.beforeSave();
    }

    this.crudService.saveRecord(this.formRecord).subscribe(apiValidation => {
      this.formLoading = false;
      this.tableLoading = false;
      if (apiValidation.success) {
        if (reload && this.autoReloadTimeOut <= 0)
          this.loadRecords();
        this.formSubmitted = false;
        this.closeDialogForm();
      } else {
        this.globalErrorMessage = apiValidation.message;
      }
      this.refreshContextMenu();
      this.markForCheck();
      this.onSave.emit(apiValidation.success);
    });
  }

  loadRecords(request?: TableRequest, ignoreLoading: boolean = false) {
    if (request) {
      this.tableCurrentRequest = request;
    }
    if (this.tableCurrentRequest) {
      setTimeout(() => {
        this.tableCurrentRequestChange.emit(this.tableCurrentRequest);
        if (!ignoreLoading)
          this.tableLoading = true;
        this.crudService.getRecords(this.tableCurrentRequest).subscribe(tableRecordResult => {
            if (tableRecordResult.success) {
              this.onTableResultGet.emit(tableRecordResult);
              this.totalRecords = tableRecordResult.total;
              if (ignoreLoading) {
                this.records = tableRecordResult.records.map(record => {
                  const existingRecord = this.records.find(r => r[this.fieldId] === record[this.fieldId]);
                  return existingRecord ? Object.assign(existingRecord, record) : record;
                });
              } else {
                this.records.splice(0);
                if (tableRecordResult.records)
                  this.records.push(...tableRecordResult.records);
                this.records = [...this.records];
              }
              this.recordsChange.emit(this.records);
              this.apiMessage(tableRecordResult);
              // setTimeout(() => {
              const selectCopy = [...this.selectedRecords];
              this.selectedRecords = [];
              if (this.fieldId) {
                selectCopy.forEach(value => {
                  this.selectedRecords.push(...this.records.filter(value1 => value1[this.fieldId] == value[this.fieldId]));
                })
              }
              this.selectedRecords.forEach(value => value['selected'] = true);
              if (this.ccTable)
                this.ccTable.reloadTableValueMap();
              // });
              this.tableLoading = false;
              this.tableLoading = false;
              this.tableCurrentRequest.syncMetadata = tableRecordResult.syncMetadata;
              this.refreshContextMenu();
              this.markForCheck()
              //auto reload data
              if (this.autoReloadTimeOut > 0 && !this.destroyed) {
                timer(this.autoReloadTimeOut)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe(() => this.loadRecords(this.tableCurrentRequest, true));
              }
            } else {
              this.errorMessage(tableRecordResult.message);
              this.tableLoading = false;
            }
          }
        );
      });
    }
  }

  showFormDialog(): void {
    this.globalErrorMessage = '';
    this.dialogFormVisible = true;
    this.dialogFormVisibleChange.emit(true);
    this.reloadFieldValueMap();
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  hasOneSelection(): boolean {
    return this.selectedRecords && this.selectedRecords.length === 1;
  }

  hasMultipleSelection(): boolean {
    return this.selectedRecords && this.selectedRecords.length > 0;
  }

  getIsReadOnly() {
    if (this.writeRoles && this.writeRoles.length > 0) {
      let isReadOnly: boolean = true;
      this.writeRoles.forEach(value => {
        if (this.userRoles.includes(value)) isReadOnly = false;
      });
      return isReadOnly;
    }
    return false;
  }

  protected canDelete() {
    if (this.deleteRoles && this.deleteRoles.length > 0) {
      let canDelete: boolean = false;
      this.deleteRoles.forEach(value => {
        if (this.userRoles.includes(value)) canDelete = true;
      });
      return canDelete;
    }
    return true;
  }

  refreshContextMenu() {
    this.contextMenuConfig.forEach(menu => {
      if (menu.id === 'edit') {
        menu.disabled = !this.hasOneSelection();
      } else if (menu.id === 'clone') {
        menu.disabled = !this.hasOneSelection();
      } else if (menu.id === 'delete') {
        menu.disabled = !this.hasMultipleSelection();
      }
    });

    queueMicrotask(() => this.reloadFieldValueMap());
    this.markForCheck();
  }

  public reloadFieldValueMap(): void {
    this.isReadOnly = this.getIsReadOnly();
    setTimeout(() => {
      if (this.ccTable)
        this.ccTable.reloadTableValueMap();

      this.markForCheck();
    });
  }

}
